import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Upload } from "lucide-react";
import { useToast } from "../../context/ToastContext";

const SellerCorporateCreateProfileForm = ({
  onSubmit,
  isLoading,
  formData,
  onGoBack,
}) => {
  const { showError } = useToast();
  const [eIdFrontImage, seteIdFrontImage] = useState(null);
  const [eIdBackImage, seteIdBackImage] = useState(null);
  const [tradeLicenseImage, setTradeLicenseImage] = useState(null);
  const [trnCertificateImage, setTrnCertificateImage] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handleFileChange = (setFile) => (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showError("File size should be less than 5MB");
        return;
      }
      setFile(file);
    }
  };

  const handleFormSubmit = (data) => {
    // Clear previous validation errors
    setValidationErrors([]);

    // Validate required fields
    const requiredFields = {
      businessName: data.businessName,
      businessRegNo: data.businessRegNo,
      location: data.location,
      identificationNo: data.identificationNo,
      idExprDate: data.idExprDate,
      eIdFrontImage: eIdFrontImage,
    };

    const missingFields = Object.entries(requiredFields)
      .filter(([key, value]) => !value)
      .map(([key]) => key);

    if (missingFields.length > 0) {
      // Show all missing fields
      const fieldNames = {
        businessName: "Business Name",
        businessRegNo: "Business Registration Number",
        location: "Location",
        identificationNo: "Identification Number",
        idExprDate: "ID Expiry Date",
        eIdFrontImage: "National ID / Passport / National ID ( Front )",
      };

      const errors = missingFields.map((field) => fieldNames[field]);
      setValidationErrors(errors);
      return;
    }

    onSubmit({
      ...data,
      eIdFrontImage,
      eIdBackImage,
      tradeLicenseImage,
      trnCertificateImage,
    });
  };

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className='w-full h-full'
      method='post'
      encType='multipart/form-data'
    >
      {/* Back Button */}
      <div className='mb-4 sm:mb-6'>
        <button
          type='button'
          onClick={onGoBack}
          className='flex items-center text-[#F15A29] hover:text-[#d14a1f] font-medium transition-colors text-xs sm:text-sm'
        >
          <svg
            className='w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M15 19l-7-7 7-7'
            />
          </svg>
          Back
        </button>
      </div>

      <div className='space-y-2 sm:space-y-3'>
        <h3 className='text-lg sm:text-2xl capitalize md:text-[28px] font-bold mb-1 text-[#131313] text-center'>
          Complete Your Seller Profile
        </h3>
        <p className='text-[#13131399] capitalize pb-4 sm:pb-6 text-xs sm:text-sm md:text-[16px] text-center'>
          Please provide additional details for your corporate seller account
        </p>

        {/* Validation Errors Display */}
        {validationErrors.length > 0 && (
          <div className='bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6'>
            <h4 className='text-xs sm:text-sm font-semibold text-red-800 mb-1.5 sm:mb-2'>
              Please complete the following required fields:
            </h4>
            <div className='space-y-1'>
              {validationErrors.map((error, index) => (
                <div
                  key={index}
                  className='text-[11px] sm:text-xs md:text-sm text-red-700 flex items-center'
                >
                  <svg
                    className='w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-red-500 flex-shrink-0'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                      clipRule='evenodd'
                    />
                  </svg>
                  <span className='break-words'>{error}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4'>
        {/* Location */}
        <div className='mb-3 sm:mb-4'>
          <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
            Location <span className='text-red-500'>*</span>
          </label>
          <input
            type='text'
            placeholder='Enter your location'
            className={`w-full pl-3 sm:pl-4 pr-3 sm:pr-4 py-2.5 sm:py-3 text-[13px] sm:text-base border ${
              errors.location ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("location", {
              required: "Location is required",
            })}
          />
          {errors.location && (
            <p className='mt-1 text-[12px] sm:text-sm text-red-500'>
              {errors.location.message}
            </p>
          )}
        </div>

        {/* VAT Number */}
        <div className='mb-3 sm:mb-4'>
          <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
            VAT Number
          </label>
          <input
            type='text'
            placeholder='Enter your VAT number'
            className={`w-full pl-3 sm:pl-4 pr-3 sm:pr-4 py-2.5 sm:py-3 text-[13px] sm:text-base border ${
              errors.vatNo ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("vatNo")}
          />
        </div>
        {/* Identification Number */}
        <div className='mb-3 sm:mb-4'>
          <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
            National ID / Passport No <span className='text-red-500'>*</span>
          </label>
          <input
            type='text'
            placeholder='Enter your identification number'
            className={`w-full pl-3 sm:pl-4 pr-3 sm:pr-4 py-2.5 sm:py-3 text-[13px] sm:text-base border ${
              errors.identificationNo ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("identificationNo", {
              required: "Identification number is required",
            })}
          />
          {errors.identificationNo && (
            <p className='mt-1 text-[12px] sm:text-sm text-red-500'>
              {errors.identificationNo.message}
            </p>
          )}
        </div>
        {/* ID Expiry Date */}
        <div className='mb-3 sm:mb-4'>
          <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
            ID Expiry Date <span className='text-red-500'>*</span>
          </label>
          <input
            type='date'
            className={`w-full pl-3 sm:pl-4 pr-3 sm:pr-4 py-2.5 sm:py-3 text-[13px] sm:text-base border ${
              errors.idExprDate ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("idExprDate", {
              required: "ID expiry date is required",
            })}
          />
          {errors.idExprDate && (
            <p className='mt-1 text-[12px] sm:text-sm text-red-500'>
              {errors.idExprDate.message}
            </p>
          )}
        </div>

        {/* National ID Front */}
        <div className='mb-3 sm:mb-4'>
          <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
            Passport / National ID ( Front ){" "}
            <span className='text-red-500'>*</span>
          </label>
          <div className='border-2 border-dashed bg-white border-gray-500 rounded-lg p-3 sm:p-4 text-center'>
            <input
              type='file'
              id='eIdFrontImage'
              accept='.jpeg,.jpg,.png,.pdf'
              onChange={handleFileChange(seteIdFrontImage)}
              className='hidden'
              required
            />
            <label
              htmlFor='eIdFrontImage'
              className='cursor-pointer flex flex-col items-center'
            >
              <Upload className='w-5 h-5 sm:w-6 sm:h-6 text-gray-500 mb-1.5 sm:mb-2' />
              <span className='text-[11px] sm:text-xs md:text-sm text-gray-500'>
                JPEG, PNG, JPG, and PDF formats, up to 5MB
              </span>
              <button
                type='button'
                className='mt-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-primary-light text-white rounded-lg hover:bg-primary-dark transition-colors text-xs sm:text-sm'
                onClick={() => document.getElementById("eIdFrontImage").click()}
              >
                Browse File
              </button>
              {eIdFrontImage && (
                <p className='mt-2 text-[11px] sm:text-xs md:text-sm text-green-600 break-words px-1'>
                  {eIdFrontImage.name}
                </p>
              )}
            </label>
          </div>
          {!eIdFrontImage && (
            <p className='mt-1 text-[12px] sm:text-sm text-red-500'>
              National ID / Passport / National ID ( Front ) is required
            </p>
          )}
        </div>

        {/* National ID Back */}
        <div className='mb-3 sm:mb-4'>
          <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
            National ID / National ID ( Back )
          </label>
          <div className='border-2 border-dashed bg-white border-gray-500 rounded-lg p-3 sm:p-4 text-center'>
            <input
              type='file'
              id='eIdBackImage'
              accept='.jpeg,.jpg,.png,.pdf'
              onChange={handleFileChange(seteIdBackImage)}
              className='hidden'
            />
            <label
              htmlFor='eIdBackImage'
              className='cursor-pointer flex flex-col items-center'
            >
              <Upload className='w-5 h-5 sm:w-6 sm:h-6 text-gray-500 mb-1.5 sm:mb-2' />
              <span className='text-[11px] sm:text-xs md:text-sm text-gray-500'>
                JPEG, PNG, JPG, and PDF formats, up to 5MB
              </span>
              <button
                type='button'
                className='mt-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-primary-light text-white rounded-lg hover:bg-primary-dark transition-colors text-xs sm:text-sm'
                onClick={() => document.getElementById("eIdBackImage").click()}
              >
                Browse File
              </button>
              {eIdBackImage && (
                <p className='mt-2 text-[11px] sm:text-xs md:text-sm text-green-600 break-words px-1'>
                  {eIdBackImage.name}
                </p>
              )}
            </label>
          </div>
          {/* Back image optional - no required error */}
        </div>

        {/* Bank Name */}
        <div className='mb-3 sm:mb-4'>
          <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
            Bank Name
          </label>
          <input
            type='text'
            placeholder='Enter Bank Name'
            className={`w-full pl-3 sm:pl-4 pr-3 sm:pr-4 py-2.5 sm:py-3 text-[13px] sm:text-base border ${
              errors.bankName ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("bankName")}
          />
        </div>

        {/* Account Holder Number */}
        <div className='mb-3 sm:mb-4'>
          <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
            Account Holder Name
          </label>
          <input
            type='text'
            placeholder='Enter your Account Holder Name'
            className={`w-full pl-3 sm:pl-4 pr-3 sm:pr-4 py-2.5 sm:py-3 text-[13px] sm:text-base border ${
              errors.accountHolderName ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("accountHolderName")}
          />
        </div>

        {/* AccountNumber (Optional) */}
        <div className='mb-3 sm:mb-4'>
          <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
            Account Number
          </label>
          <input
            type='text'
            placeholder='Enter Your Account Number'
            className='w-full pl-3 sm:pl-4 pr-3 sm:pr-4 py-2.5 sm:py-3 text-[13px] sm:text-base border border-[#13131399] rounded-lg bg-white focus:outline-none'
            {...register("accountNumber")}
          />
        </div>

        {/* Account IBAN Number */}
        <div className='mb-3 sm:mb-4'>
          <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
            IBAN
          </label>
          <input
            type='text'
            placeholder='Enter your IBAN'
            className={`w-full pl-3 sm:pl-4 pr-3 sm:pr-4 py-2.5 sm:py-3 text-[13px] sm:text-base border ${
              errors.iban ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("iban")}
          />
        </div>

        {/* businessName*/}
        <div className='mb-3 sm:mb-4'>
          <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
            Business Name
          </label>
          <input
            type='text'
            placeholder='Enter Business Name'
            className={`w-full pl-3 sm:pl-4 pr-3 sm:pr-4 py-2.5 sm:py-3 text-[13px] sm:text-base border ${
              errors.businessName ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("businessName", {
              required: "Business Name is required",
            })}
          />
          {errors.businessName && (
            <p className='mt-1 text-[12px] sm:text-sm text-red-500'>
              {errors.businessName.message}
            </p>
          )}
        </div>

        {/* Account business RegNo */}
        <div className='mb-3 sm:mb-4'>
          <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
            Business Reg No <span className='text-red-500'>*</span>
          </label>
          <input
            type='text'
            placeholder='Enter Your Business Reg No'
            className={`w-full pl-3 sm:pl-4 pr-3 sm:pr-4 py-2.5 sm:py-3 text-[13px] sm:text-base border ${
              errors.businessRegNo ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("businessRegNo", {
              required: "Business Registration Number is required",
            })}
          />
          {errors.businessRegNo && (
            <p className='mt-1 text-[12px] sm:text-sm text-red-500'>
              {errors.businessRegNo.message}
            </p>
          )}
        </div>

        {/* Account Trade License Image */}
        <div className='mb-3 sm:mb-4'>
          <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
            Trade License Image
          </label>
          <div className='border-2 border-dashed bg-white border-gray-500 rounded-lg p-3 sm:p-4 text-center'>
            <input
              type='file'
              id='tradeLicenseImage'
              accept='.jpeg,.jpg,.png,.pdf'
              onChange={handleFileChange(setTradeLicenseImage)}
              className='hidden'
            />
            <label
              htmlFor='tradeLicenseImage'
              className='cursor-pointer flex flex-col items-center'
            >
              <Upload className='w-5 h-5 sm:w-6 sm:h-6 text-gray-500 mb-1.5 sm:mb-2' />
              <span className='text-[11px] sm:text-xs md:text-sm text-gray-500'>
                JPEG, PNG, JPG, and PDF formats, up to 5MB
              </span>
              <button
                type='button'
                className='mt-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-primary-light text-white rounded-lg hover:bg-primary-dark transition-colors text-xs sm:text-sm'
                onClick={() =>
                  document.getElementById("tradeLicenseImage").click()
                }
              >
                Browse File
              </button>
              {tradeLicenseImage && (
                <p className='mt-2 text-[11px] sm:text-xs md:text-sm text-green-600 break-words px-1'>
                  {tradeLicenseImage.name}
                </p>
              )}
            </label>
          </div>
        </div>

        {/* Account TRN Certificate Image */}
        <div className='mb-3 sm:mb-4'>
          <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
            TRN Certificate Image
          </label>
          <div className='border-2 border-dashed bg-white border-gray-500 rounded-lg p-3 sm:p-4 text-center'>
            <input
              type='file'
              id='trnCertificateImage'
              accept='.jpeg,.jpg,.png,.pdf'
              onChange={handleFileChange(setTrnCertificateImage)}
              className='hidden'
            />
            <label
              htmlFor='trnCertificateImage'
              className='cursor-pointer flex flex-col items-center'
            >
              <Upload className='w-5 h-5 sm:w-6 sm:h-6 text-gray-500 mb-1.5 sm:mb-2' />
              <span className='text-[11px] sm:text-xs md:text-sm text-gray-500'>
                JPEG, PNG, JPG, and PDF formats, up to 5MB
              </span>
              <button
                type='button'
                className='mt-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-primary-light text-white rounded-lg hover:bg-primary-dark transition-colors text-xs sm:text-sm'
                onClick={() =>
                  document.getElementById("trnCertificateImage").click()
                }
              >
                Browse File
              </button>
              {trnCertificateImage && (
                <p className='mt-2 text-[11px] sm:text-xs md:text-sm text-green-600 break-words px-1'>
                  {trnCertificateImage.name}
                </p>
              )}
            </label>
          </div>
        </div>
      </div>

      {/* Submit button */}
      <button
        type='submit'
        disabled={isLoading}
        className={`w-full border border-[#F15A29] bg-white text-primary-dark py-2.5 sm:py-3 rounded-lg hover:bg-primary-dark hover:text-white transition-all duration-200 font-medium mb-3 sm:mb-4 text-[14px] sm:text-base ${
          isLoading ? "opacity-90 cursor-not-allowed" : ""
        }`}
      >
        {isLoading ? "Submitting..." : "Complete Seller Registration"}
      </button>
    </form>
  );
};

export default SellerCorporateCreateProfileForm;
